<template>
  <div class="gamify">
    <section class="header">
      <div class="title">
        <span class="spark">✨</span>
        <h2>XP Arcade</h2>
        <span class="spark">⚡</span>
      </div>

      <div class="ring-wrap" :title="`Level ${level} • ${xp} XP`">
        <svg viewBox="0 0 120 120" class="ring">
          <circle class="bg" cx="60" cy="60" r="54" />
          <circle
            class="fg"
            cx="60" cy="60" r="54"
            :style="ringStyle"
          />
          <text x="60" y="58" text-anchor="middle" class="lvl">Lv {{ level }}</text>
          <text x="60" y="78" text-anchor="middle" class="xp">{{ xp }} XP</text>
        </svg>
      </div>
    </section>

    <section class="slots">
      <div class="reel" :class="{spin: spinning}">
        <div class="cell">{{ symbols[cur[0]] }}</div>
      </div>
      <div class="reel" :class="{spin: spinning}">
        <div class="cell">{{ symbols[cur[1]] }}</div>
      </div>
      <div class="reel" :class="{spin: spinning}">
        <div class="cell">{{ symbols[cur[2]] }}</div>
      </div>
    </section>

    <section class="controls">
      <button
        class="btn spin"
        :disabled="spinning"
        @click="spin"
        :aria-busy="String(spinning)"
      >
        <span v-if="!spinning">🎰 Spin</span>
        <span v-else>Spinning…</span>
      </button>
      <div class="hint">Match symbols to earn bonus XP!</div>
      <div v-if="message" class="toast" :class="toastClass">{{ message }}</div>
    </section>

    <section class="feed">
      <h4>Recent Wins</h4>
      <ul>
        <li v-for="(w,i) in recent" :key="i">
          <span class="when">{{ w.when }}</span>
          <span class="what">{{ w.what }}</span>
          <span class="amt">+{{ w.xp }} XP</span>
        </li>
      </ul>
    </section>

    <!-- confetti canvas -->
    <canvas ref="confetti" class="confetti"></canvas>
  </div>
</template>

<script>
export default {
  name: "GamifyOverlay",
  data() {
    return {
      symbols: ["⭐", "⚡", "💎", "🎉", "🪙", "🔥", "🚀", "🌟"],
      cur: [0, 1, 2],
      spinning: false,
      message: "",
      toastType: "info", // info | win | big
      recent: [],
      confettiRAF: 0,
      confettiParts: [],
    };
  },
  computed: {
    xp() { return this.$store.state.xp; },
    level() { return this.$store.state.level; },
    progress() { return this.$store.getters.levelProgress; }, // 0..1

    ringStyle() {
      // SVG circumference
      const r = 54;
      const C = 2 * Math.PI * r;
      const dash = Math.max(0.001, this.progress) * C;
      return {
        strokeDasharray: `${dash} ${C - dash}`,
      };
    },
    toastClass() {
      return {
        info: this.toastType === "info",
        win: this.toastType === "win",
        big: this.toastType === "big",
      };
    },
  },
  mounted() {
    this.resizeConfetti();
    window.addEventListener("resize", this.resizeConfetti);
    // gentle entrance message
    this.flash("Spin the reels to earn bonus XP!", "info");
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeConfetti);
    cancelAnimationFrame(this.confettiRAF);
  },
  methods: {
    spin() {
      if (this.spinning) return;
      this.spinning = true;
      this.message = "";
      this.toastType = "info";

      // randomize target indices
      const t0 = Math.floor(Math.random() * this.symbols.length);
      const t1 = Math.floor(Math.random() * this.symbols.length);
      const t2 = Math.floor(Math.random() * this.symbols.length);

      // staggered stopping for drama
      const d0 = 600 + Math.random() * 300;
      const d1 = d0 + 300 + Math.random() * 300;
      const d2 = d1 + 300 + Math.random() * 300;

      const spinTo = (idx, delay, reel) => {
        // quick “blur” effect by swapping fast then stop
        const roll = setInterval(() => {
          this.cur.splice(reel, 1, Math.floor(Math.random() * this.symbols.length));
        }, 60);
        setTimeout(() => {
          clearInterval(roll);
          this.cur.splice(reel, 1, idx);
        }, delay);
      };

      spinTo(t0, d0, 0);
      spinTo(t1, d1, 1);
      spinTo(t2, d2, 2);

      setTimeout(() => {
        const xp = this.evaluateWin(t0, t1, t2);
        if (xp > 0) {
          this.$store.dispatch("addXP", xp);
        }
        this.spinning = false;
      }, d2 + 60);
    },

    evaluateWin(a, b, c) {
      let xp = 0;
      if (a === b && b === c) {
        xp = 150;
        this.flash("JACKPOT! Triple match!", "big");
        this.confetti();
      } else if (a === b || a === c || b === c) {
        xp = 50;
        this.flash("Nice! Double match!", "win");
        this.confetti(120);
      } else {
        xp = 10;
        this.flash("Small boost for the try!", "info");
      }
      this.recent.unshift({
        when: new Date().toLocaleTimeString(),
        what: `${this.symbols[a]} ${this.symbols[b]} ${this.symbols[c]}`,
        xp,
      });
      this.recent = this.recent.slice(0, 8);
      return xp;
    },

    flash(msg, type = "info") {
      this.message = msg;
      this.toastType = type;
      setTimeout(() => {
        // keep big wins visible a bit longer
        if (this.toastType !== "big") this.message = "";
      }, type === "big" ? 4000 : 2200);
    },

    // ===== Confetti (simple, no libs) =====
    resizeConfetti() {
      const c = this.$refs.confetti;
      if (!c) return;
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      c.width = c.clientWidth * dpr;
      c.height = c.clientHeight * dpr;
      const ctx = c.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    },
    confetti(count = 180) {
      const c = this.$refs.confetti;
      if (!c) return;
      const w = c.clientWidth;
      const h = c.clientHeight;
      this.confettiParts = Array.from({ length: count }).map(() => ({
        x: w / 2,
        y: h / 3,
        vx: (Math.random() - 0.5) * (6 + Math.random() * 6),
        vy: - (4 + Math.random() * 6),
        g: 0.18 + Math.random() * 0.07,
        s: 3 + Math.random() * 4,
        r: Math.random() * Math.PI,
        col: this.randColor(),
        life: 60 + Math.random() * 60
      }));
      cancelAnimationFrame(this.confettiRAF);
      const tick = () => {
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, w, h);
        this.confettiParts.forEach(p => {
          p.vy += p.g;
          p.x += p.vx;
          p.y += p.vy;
          p.r += 0.1;
          p.life -= 1;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.r);
          ctx.fillStyle = p.col;
          ctx.fillRect(-p.s/2, -p.s/2, p.s, p.s * 1.6);
          ctx.restore();
        });
        this.confettiParts = this.confettiParts.filter(p => p.life > 0 && p.y < h + 20);
        if (this.confettiParts.length > 0) {
          this.confettiRAF = requestAnimationFrame(tick);
        }
      };
      this.confettiRAF = requestAnimationFrame(tick);
    },
    randColor() {
      const palette = ["#00E0FF", "#7C6CFF", "#FFCE6B", "#FF7BC8", "#8AF0B5"];
      return palette[Math.floor(Math.random() * palette.length)];
    },
  }
};
</script>

<style scoped>
.gamify{
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 14px;
  height: 100%;
  position: relative;
  color: #eef3ff;
}

/* Header with ring */
.header{
  display:flex; align-items:center; justify-content:space-between;
  background: linear-gradient(180deg, rgba(255,255,255,.05), rgba(255,255,255,.02));
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 14px;
  padding: 10px 12px;
}
.title{
  display:flex; align-items:center; gap:10px;
}
.title h2{ margin: 0; letter-spacing:.4px; font-weight: 700; }
.spark{ filter: drop-shadow(0 0 6px rgba(255,255,255,.35)); }

.ring-wrap{ width: 120px; height: 120px; }
.ring{ width: 120px; height: 120px; transform: rotate(-90deg); }
.ring .bg{
  fill: none; stroke: rgba(255,255,255,.15); stroke-width: 10; stroke-linecap: round;
}
.ring .fg{
  fill: none; stroke: url(#grad);
  stroke: linear-gradient(90deg, #00E0FF, #7C6CFF);
  stroke: #00E0FF; /* fallback */
  stroke-width: 10; stroke-linecap: round;
  transition: stroke-dasharray .35s ease;
}
.ring .lvl, .ring .xp{
  transform: rotate(90deg);
  fill: #e9eaff; font-weight: 700;
}
.ring .lvl{ font-size: 16px; }
.ring .xp{ font-size: 11px; opacity:.9; }

/* Slots */
.slots{
  display:grid; grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.reel{
  background: linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,.02));
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 14px;
  padding: 18px 6px;
  display:flex; align-items:center; justify-content:center;
  height: 110px;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.05), 0 18px 60px rgba(0,0,0,.25);
  position: relative;
  overflow: hidden;
}
.cell{
  font-size: 46px;
  filter: drop-shadow(0 4px 18px rgba(0,224,255,.25));
  transform: translateZ(0);
}
.reel.spin .cell{
  animation: wobble .12s linear infinite;
}
@keyframes wobble{
  0%{ transform: translateY(-1px) }
  50%{ transform: translateY(1px) }
  100%{ transform: translateY(-1px) }
}

/* Controls / Toast */
.controls{
  display:flex; align-items:center; gap:12px; flex-wrap: wrap;
}
.btn.spin{
  padding:10px 18px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.18);
  background: linear-gradient(180deg, rgba(0,224,255,.18), rgba(124,108,255,.14));
  color: #0A0D1A; font-weight: 800;
  text-shadow: 0 1px 0 rgba(255,255,255,.3);
  cursor: pointer;
  transition: transform .12s ease, box-shadow .2s ease, filter .12s ease, opacity .2s ease;
  box-shadow: 0 14px 40px rgba(0,224,255,.18);
}
.btn.spin[disabled]{
  opacity:.6; cursor: not-allowed; filter: grayscale(.15);
}
.btn.spin:hover:not([disabled]){
  transform: translateY(-1px);
  box-shadow: 0 18px 60px rgba(0,224,255,.24);
}
.hint{ opacity:.8; font-size: 12px; }

.toast{
  margin-left:auto;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.06);
}
.toast.info{ color: #cfe9ff; }
.toast.win{ color: #cfffde; border-color: rgba(138,240,181,.4); box-shadow: 0 0 28px rgba(138,240,181,.18) inset; }
.toast.big{ color: #ffe9c7; border-color: rgba(255,206,107,.5); box-shadow: 0 0 32px rgba(255,206,107,.22) inset; }

/* Feed */
.feed{
  background: linear-gradient(180deg, rgba(255,255,255,.05), rgba(255,255,255,.02));
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 14px;
  padding: 10px 12px;
}
.feed h4{ margin: 0 0 8px; }
.feed ul{ list-style: none; margin: 0; padding: 0; display:grid; grid-template-columns: 1fr; gap: 6px; }
.feed li{
  display:grid; grid-template-columns: 120px 1fr 80px; gap: 8px;
  background: rgba(255,255,255,.04);
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 10px;
  padding: 6px 8px;
  align-items:center;
}
.feed .when{ opacity:.85; font-size: 12px; }
.feed .what{ letter-spacing:.3px; }
.feed .amt{ font-weight: 800; color: #8AF0B5; text-align:right; }

/* Confetti canvas fills component */
.confetti{
  position:absolute; inset: 0; pointer-events: none;
}
</style>
